/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { ProductWarehouseInventoryInformation, SimpleProduct } from '@msdyn365-commerce/retail-proxy';

import { IGridSettings } from '@msdyn365-commerce/core-internal';

import { getProductsByCategoryId } from '../../actions/helper-functions';

import { getByCustomerBTGPTransactionAsync, checkAvailableSerialAsync, getProductEventsByIdsAsync } from '../../actions/DataActionExtension.g';
import { getEstimatedAvailabilityAsync, getAttributeValuesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

import { IEcommEventProductsData } from './ecomm-event-products.data';
import { IEcommEventProductsProps } from './ecomm-event-products.props.autogenerated';

const moment = require('moment');

export interface IEcommEventProductsViewProps extends IEcommEventProductsProps<IEcommEventProductsData> {
  gridSettings: IGridSettings;
  products: SimpleProduct[];
  productsHaveAssociatedEvents: boolean[];
  warehouse: ProductWarehouseInventoryInformation;
  isRegistered: boolean[];
  serialAvailable: boolean[];
}

/**
 *
 * EcommEventProducts component
 * @extends {React.PureComponent<IEcommEventProductsProps<IEcommEventProductsData>>}
 */
class EcommEventProducts extends React.PureComponent<IEcommEventProductsProps<IEcommEventProductsData>> {
  public state: any = { products: [], productsAvailabilities: {}, registered: [], serialAvailable: [], productsHaveAssociatedEvents: [] };

  public async componentDidMount() {
    const { context, context: { request: { user } } } = this.props;

    if (typeof window !== 'undefined') {
      const parameters = window.location.search;
      const categoryId = new URLSearchParams(parameters).get('id');

      if (categoryId) {
        const result = await getProductsByCategoryId(context, parseInt(categoryId)).catch(error => console.log('[EcommEventProducts] getProductsByCategoryId error:', error));

        if (result && result.length > 0) {
          const productRegistered: boolean[] = [];
          const productSerialAvailable: boolean[] = [];
          const productIds: number[] = [];

          for (let i = 0; i < result.length; i++) {
            productRegistered.push(false);
            productSerialAvailable.push(true);

            const attributesList = await getAttributeValuesAsync({callerContext: context.actionContext }, result[`${i}`].RecordId, context.request.apiSettings.channelId, context.request.apiSettings.catalogId);

            const hasSerialIndex= attributesList.findIndex(attr => attr.Name?.includes('Has Serial'));

            if (hasSerialIndex >= 0) {
              const itemHasSerial = attributesList[`${hasSerialIndex}`].BooleanValue ? attributesList[`${hasSerialIndex}`].BooleanValue! : false;

              if (itemHasSerial) {
                const serialAvalability = await checkAvailableSerialAsync({ callerContext: this.props.context.actionContext }, this.props.config.isUat, result[`${i}`].RecordId.toString()!);

                if (serialAvalability && typeof serialAvalability === 'object' && serialAvalability['value'] == false) {
                  productSerialAvailable[`${i}`] = false;
                }
              }
            }

            productIds.push(result[`${i}`].RecordId);
          }

          if (user.isAuthenticated) {
            for (let i = 0; i < result.length; i++) {
              const records = await getByCustomerBTGPTransactionAsync({ callerContext: context.actionContext }, user.customerAccountNumber!, user.emailAddress!, String(result[`${i}`].RecordId), this.props.config.isUat);

              if (records.length > 0) {
                productRegistered[`${i}`] = true;
              }
            }
          }

          const warehouse = await getEstimatedAvailabilityAsync({ callerContext: context.actionContext }, { ProductIds: productIds, DefaultWarehouseOnly: true });

          let startDate = new Date();
          startDate.setHours(0, 0, 0, 0);
          let productIdsStr = productIds.join(",");
          let productsEventsJsonStr = await getProductEventsByIdsAsync({callerContext: context.actionContext}, this.props.config.isUat, startDate.toISOString(), productIdsStr);
          let productsEvents = JSON.parse(productsEventsJsonStr);
          let productsHaveAssociatedEvents = [];
          for (let i = 0; i< productsEvents.length; i++){
            if (productsEvents[i].length == 0){
              productsHaveAssociatedEvents.push(false);
              continue;
            }
            let productEvents = productsEvents[i];
            let latestEvent = JSON.parse(productEvents[productEvents.length - 1]);
            let eventStartDate = moment(latestEvent['StartDateTime']);
            let currentTime = moment();
      
            if (currentTime.diff(eventStartDate, 'd') > 0) {
              productsHaveAssociatedEvents.push(false);
            }
            else{
              productsHaveAssociatedEvents.push(true);
            }
          }

          this.setState({ products: result, productsHaveAssociatedEvents: productsHaveAssociatedEvents, productsAvailabilities: warehouse, registered: productRegistered, serialAvailable: productSerialAvailable });
        }
      }
    }
  }

  public render(): JSX.Element | null {
    const EcommEventProductsViewProps = {
      ...this.props,
      products: this.state.products,
      productsHaveAssociatedEvents: this.state.productsHaveAssociatedEvents,
      warehouse: this.state.productsAvailabilities,
      isRegistered: this.state.registered,
      serialAvailable: this.state.serialAvailable
    };
  
    return this.props.renderView(EcommEventProductsViewProps);
  }
}

export default EcommEventProducts;